import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import TripCard from "./TripCard";
import { PAGE_CONTAINER } from "../../layout/pageWidth";

// The cards are laid out in two rows that slide sideways together: the grid fills down a column, then moves on to the next one. A column
// is a card wide — four columns show from `xl` up (the gaps are 1.25rem), three from `lg`, two from `sm`, and on a phone one and a bit
// — and any more are reached by sliding.
const COLUMN_WIDTH =
  "auto-cols-[260px] sm:auto-cols-[calc((100%-1.25rem)/2)] lg:auto-cols-[calc((100%-2.5rem)/3)] xl:auto-cols-[calc((100%-3.75rem)/4)]";
const GRID = `grid grid-flow-col grid-rows-2 gap-5 ${COLUMN_WIDTH}`;

const TripCardSkeleton = () => <div className="h-[300px] animate-pulse rounded-2xl bg-gray-200" />;

/**
 * One curated band of trips (Domestic / International): a header with the icon, the title and a View All button, then a row of
 * cards that slides sideways, with an arrow to move along it. Renders its own loading and empty states so a slow or empty section
 * never blocks the page — the previous implementation gated the entire route behind one spinner.
 */
const TripSection = ({
  icon,
  title,
  subtitle,
  posts,
  loading,
  seeAllLabel,
  seeAllTo,
  emptyMessage,
  tone = "light",
}) => {
  const isEmpty = !loading && posts.length === 0;

  // Whether there is more of the row to the right of what shows — the arrow is only there then.
  const rowRef = useRef(null);
  const [more, setMore] = useState(false);
  const measure = useCallback(() => {
    const el = rowRef.current;
    setMore(Boolean(el) && el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);
  // Measured when the row or any card in it changes size — which includes the first time they are laid out.
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, posts.length, loading]);

  const slide = () => rowRef.current?.scrollBy({ left: rowRef.current.clientWidth * 0.8, behavior: "smooth" });

  return (
    <section className={tone === "muted" ? "bg-gray-50" : "bg-white"}>
      <div className={`${PAGE_CONTAINER} pt-6 pb-10 sm:pt-8 sm:pb-12`}>
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3.5">
            <span
              className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600 ring-1 ring-teal-100"
              aria-hidden="true"
            >
              {icon}
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">{title}</h2>
              <p className="mt-0.5 text-xs text-gray-500 sm:text-sm">{subtitle}</p>
            </div>
          </div>

          {seeAllTo && !isEmpty && (
            <Link
              to={seeAllTo}
              className="group inline-flex shrink-0 items-center gap-1.5 rounded-full border border-teal-600 bg-white px-4 py-1.5 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-50 sm:text-sm"
            >
              {seeAllLabel}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          )}
        </div>

        {loading && posts.length === 0 ? (
          <div className={`${GRID} overflow-hidden pb-2`}>
            {Array.from({ length: 8 }).map((_, i) => (
              <TripCardSkeleton key={i} />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
            <p className="mb-1 font-semibold text-gray-900">Nothing here yet</p>
            <p className="mx-auto max-w-sm text-sm text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          <div className="relative">
            <div
              ref={rowRef}
              onScroll={measure}
              className={`-mx-1 snap-x snap-mandatory overflow-x-auto px-1 pb-4 pt-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${GRID}`}
            >
              {posts.map((post, i) => (
                <div key={post.id || post.slug || i} className="snap-start">
                  <TripCard post={post} />
                </div>
              ))}
            </div>
            {more && (
              <button
                type="button"
                onClick={slide}
                aria-label={`Show more ${title.toLowerCase()}`}
                className="absolute right-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-gray-700 shadow-lg ring-1 ring-black/5 transition-colors hover:bg-gray-50 sm:flex"
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default TripSection;
